import { FC } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRightIcon } from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";

import { PropsWithClassName } from "@/types/PropsWithClassName";
import { cn } from "@/utils/classNames.ts";
import { passToRoute } from "@/utils/passToRoute.ts";
import Routes from "@/router/Routes.ts";
import { dateRange } from "$/utils/dayjs";

type TripBlockProps = PropsWithClassName<{
  id: string;
  name: string;
  arrivalDate?: string | null;
  departureDate?: string | null;
  duration?: number | null;
  badge?: string;
  onClick?: () => void;
}>;

const TripBlock: FC<TripBlockProps> = ({
  className,
  id,
  name,
  arrivalDate,
  departureDate,
  duration,
  badge,
  onClick,
}) => {
  const { t } = useTranslation();

  const dateOrDuration =
    arrivalDate && departureDate
      ? dateRange(arrivalDate, departureDate, true)
      : duration
        ? duration > 1
          ? t("label.days_plural", { count: duration })
          : t("label.days_singular", { count: duration })
        : null;

  return (
    <Link
      to={passToRoute(Routes.TRIP, { id })}
      onClick={onClick}
      className={cn(
        "group flex min-h-[72px] w-full items-center gap-3 rounded-xl border border-primary/20 bg-white px-4 py-3 shadow-sm transition-all hover:-translate-y-0.5 hover:border-primary/50 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
        className,
      )}
      aria-label={
        badge
          ? `${t("button.open_trip")}: ${name} (${badge})`
          : `${t("button.open_trip")}: ${name}`
      }
    >
      <span className="min-w-0 flex-1">
        <span className="block truncate font-feature text-2xl font-bold italic text-base-content">
          {name}
        </span>

        {dateOrDuration ? (
          <span className="mt-0.5 block text-xs text-text-secondary">
            {dateOrDuration}
          </span>
        ) : null}

        {badge ? (
          <span className="mt-1 inline-flex items-center rounded-full bg-error/10 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-error">
            {badge}
          </span>
        ) : null}
      </span>

      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-white">
        <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
      </span>
    </Link>
  );
};

export default TripBlock;
